// Round navigator (UI-SPEC §8): 44×36 chevron buttons + centered label,
// plus the round dropdown on the label (build brief). "ROUND JUMP VIA
// NAVIGATOR ONLY · NO SECOND MECHANISM" — this component IS that mechanism.
import React, { useEffect, useRef, useState } from "react";
import { C } from "./competitionsUi";

const Chevron = ({ dir = "left", compact = false }) => (
  <svg
    width={compact ? 7 : 8}
    height={compact ? 12 : 13}
    viewBox="0 0 8 14"
    fill="none"
    aria-hidden="true"
    style={dir === "right" ? { transform: "scaleX(-1)" } : undefined}
  >
    <path
      d="M6.5 1L1.5 7L6.5 13"
      stroke={C.ink600}
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const navButtonStyle = {
  width: 44,
  height: 36,
  border: `1px solid ${C.border}`,
  borderRadius: 3,
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
};

const CompetitionRoundNavigator = ({
  rounds = [],
  round,
  onRoundChange,
  labelOf,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!rounds.length) return null;
  const index = rounds.findIndex((r) => r.round === round);
  const current = index >= 0 ? rounds[index] : rounds[0];
  const prev = index > 0 ? rounds[index - 1] : null;
  const next = index >= 0 && index < rounds.length - 1 ? rounds[index + 1] : null;

  const jump = (target) => {
    setOpen(false);
    if (target && onRoundChange) onRoundChange(target.round);
  };

  return (
    <div
      ref={rootRef}
      style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}
    >
      <button
        type="button"
        aria-label="Previous round"
        disabled={!prev}
        onClick={() => jump(prev)}
        style={{ ...navButtonStyle, opacity: prev ? 1 : 0.4, cursor: prev ? "pointer" : "default" }}
      >
        <Chevron dir="left" compact={compact} />
      </button>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          minWidth: compact ? 78 : 120,
          textAlign: "center",
          fontSize: compact ? 13.5 : 14.5,
          fontWeight: 600,
          color: C.ink900,
          fontVariantNumeric: "tabular-nums",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "8px 4px",
        }}
      >
        {labelOf(current)}
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 30,
            minWidth: compact ? 140 : 170,
            maxHeight: 280,
            overflowY: "auto",
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(15,23,42,.08)",
          }}
        >
          {rounds.map((r) => {
            const active = r.round === current.round;
            return (
              <button
                key={String(r.round)}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => jump(r)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  padding: "10px 14px",
                  minHeight: 40,
                  fontSize: 13.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? C.accent : C.ink700,
                  fontVariantNumeric: "tabular-nums",
                  background: active ? C.rowHoverBg : "#fff",
                  border: "none",
                  borderBottom: `1px solid ${C.rowDivider}`,
                  cursor: "pointer",
                }}
              >
                {labelOf(r)}
              </button>
            );
          })}
        </div>
      )}
      <button
        type="button"
        aria-label="Next round"
        disabled={!next}
        onClick={() => jump(next)}
        style={{ ...navButtonStyle, opacity: next ? 1 : 0.4, cursor: next ? "pointer" : "default" }}
      >
        <Chevron dir="right" compact={compact} />
      </button>
    </div>
  );
};

export default CompetitionRoundNavigator;
